import {
    ReadonlyMat3,
    ReadonlyMat4,
    ReadonlyVec3,
    mat4,
    quat,
    vec2,
    vec3,
} from 'gl-matrix';
import { VertexArrayObject } from '~/engine/core/utils/VertexArrayObject';
import { SignedDistanceField } from '../../core/models/SignedDistanceField';
import { SDFMesh } from '../../util/algorithms/sdf/SDFMesh';
import { RotatedBoundingBox } from '../common/RotatedBoundingBox';

import { ContourSequence } from './ContourSequence';
import { Grid } from '../common/Grid';

/**
 * {@link ContourTexture} represents the contour data using a {@link SignedDistanceField} (SDF) texture.\
 * This presentation is used by most contouring tools and visualizations.
 */
export class ContourTexture {
    /**
     * The default {@link margin} that is added around each side of the contour data when generating texture.\
     * The unit is millimeters.
     */
    public static DEFAULT_MARGIN = 10;

    /**
     * The amount of padding added to each side of the contour data in millimeters.\
     * This is useful to support better visualization.\
     * Padding provides smoother contours and improves the quality of contouring tools
     * and operations such as interpolation.
     */
    private _margin: number;

    private _boundingBox: RotatedBoundingBox;

    /**
     * The Signed Distance Field that contains the WebGL rendering texture data.
     */
    private _sdf: SignedDistanceField;

    /**
     * Cached SDF values for CPU calculations.
     */
    private _pixels: Uint8Array[] | undefined;

    private readonly imageToPatient: mat4;

    private readonly _patientToImage: mat4;

    // grid geometry of the Signed Distance Field with transformation from patient coordinates to voxel space
    private _textureGrid: Grid;

    private readonly _segmentResolution: number;

    public constructor(
        private readonly _imageGrid: Grid,
        bb?: RotatedBoundingBox
    ) {
        this._margin = ContourTexture.DEFAULT_MARGIN;
        this._boundingBox = bb
            ? new RotatedBoundingBox(bb)
            : new RotatedBoundingBox();
        this.imageToPatient = mat4.create();
        this._patientToImage = mat4.create();

        if (this._imageGrid.resX !== this._imageGrid.resY) {
            console.warn(
                `ContourTexture: resX and resY are not equal ${this._imageGrid.resX}, ${this._imageGrid.resY}`
            );
        }

        this._segmentResolution = this._imageGrid.resX;

        const sizePx = vec3.create();
        vec3.set(
            sizePx,
            Math.ceil(this._boundingBox.size[0] / this._segmentResolution),
            Math.ceil(this._boundingBox.size[1] / this._segmentResolution),
            Math.round(this._boundingBox.size[2] / _imageGrid.resZ) + 1
        );
        this._sdf = new SignedDistanceField(sizePx);
        if (!this._boundingBox.min || !this._boundingBox.max) {
            this._textureGrid = new Grid(
                sizePx[0],
                sizePx[1],
                sizePx[2],
                this._segmentResolution,
                this._segmentResolution,
                _imageGrid.resZ,
                _imageGrid.getTransformation()
            );
        } else {
            // ensure the grid aligns with image grid. TODO: simplify this to use the image grid directly
            const bbMinImageGrid = this._imageGrid.toVoxelSpace(
                this._boundingBox.min
            );
            const bbMinImageGridClamped = this._imageGrid.toWorldSpace(
                vec3.floor(bbMinImageGrid, bbMinImageGrid)
            );
            this._boundingBox.min[2] = bbMinImageGridClamped[2];

            const bbMaxImageGrid = this._imageGrid.toVoxelSpace(
                this._boundingBox.max
            );
            const bbMaxImageGridClamped = this._imageGrid.toWorldSpace(
                vec3.floor(bbMaxImageGrid, bbMaxImageGrid)
            );
            this._boundingBox.max[2] = bbMaxImageGridClamped[2];
            mat4.fromRotationTranslationScale(
                this.imageToPatient,
                quat.fromMat3(quat.create(), this.rotation),
                vec3.transformMat3(
                    vec3.create(),
                    this._boundingBox.center,
                    this._boundingBox.rotation
                ),
                vec3.scale(vec3.create(), this._boundingBox.size, 0.5)
            );

            mat4.invert(this._patientToImage, this.imageToPatient);

            this._textureGrid = new Grid(
                sizePx[0],
                sizePx[1],
                sizePx[2],
                this._segmentResolution,
                this._segmentResolution,
                this._imageGrid.resZ,
                mat4.fromRotationTranslation(
                    mat4.create(),
                    quat.fromMat3(quat.create(), this.rotation),
                    this._boundingBox.min
                )
            );
        }
    }

    /**
     * TODO: fix or remove this partial cloning, SDF is for example not cloned or otherwise handled.
     */
    public set(ref: ContourTexture) {
        this._boundingBox = new RotatedBoundingBox(ref._boundingBox);
        if (!this._boundingBox.min || !this._boundingBox.max) {
            throw new Error(
                `Attempt to generate slices for ContourTexture with an empty BoundingBox`
            );
        }
        this._sdf = new SignedDistanceField(ref._sdf.size);
        this.invalidateCachedPixels();
    }

    public setTexture(ref: ContourTexture) {
        this._boundingBox = new RotatedBoundingBox(ref._boundingBox);
        if (!this._boundingBox.min || !this._boundingBox.max) {
            throw new Error(
                `Attempt to generate slices for ContourTexture with an empty BoundingBox`
            );
        }
        this._sdf = ref._sdf;
        this._textureGrid = Grid.clone(ref.textureGrid);
        mat4.copy(this.imageToPatient, ref.imageToPatient);
        mat4.copy(this._patientToImage, ref._patientToImage);
    }

    /**
     * {@link ReadonlyMat3 rotation} from ImageSpace to PatientSpace.\
     * This is the {@link RotatedBoundingBox.rotation rotation} of the {@link _boundingBox}.
     */
    public get rotation(): ReadonlyMat3 {
        return this._imageGrid.getOrientation();
    }

    /**
     * The common Grid where the texture is contained in.\
     * Equals the image Grid.\
     */
    public get grid(): Grid {
        return this._imageGrid;
    }

    private _volumeCachedCm3: number | undefined;
    public get volumeCm3(): number {
        if (this._volumeCachedCm3 === undefined) {
            const pixelSize =
                this._textureGrid.resX *
                this._textureGrid.resY *
                this._textureGrid.resZ;
            const pixels = this.pixels;
            let volumeMm3 = 0;
            for (let z = 0; z < pixels.length; z++) {
                const slice = pixels[z];
                for (let idx = 0; idx < slice.length; idx++) {
                    if (slice[idx] > 127) {
                        // TODO: this is coarse approximation, can be improved with interpolation around the neighboring SDF values
                        volumeMm3 += pixelSize;
                    }
                }
            }
            this._volumeCachedCm3 = volumeMm3 / 1000; // mm^3 to cm^3
        }
        return this._volumeCachedCm3;
    }

    /**
     * Method to get the PatientSpace {@link ReadonlyVec3 coordinates} of this {@link ContourTexture}'s center point.\
     * This is the {@link RotatedBoundingBox.center centerPoint} of the {@link _boundingBox} rotated by the {@link rotation} to convert from ImageSpace to PatientSpace.
     */
    public get center(): ReadonlyVec3 {
        return this._imageGrid.rotate(this._boundingBox.center);
    }

    public get widthMm(): number {
        return this._boundingBox.size[0];
    }

    public get heightMm(): number {
        return this._boundingBox.size[1];
    }

    public get sizePx(): ReadonlyVec3 {
        return this._sdf ? this._sdf.size : vec3.create();
    }

    /**
     * Method to get the planar resolution of the texture SDF.\
     * The resolution should be the same as the image resolution.\
     * The unit is mm/px.
     */
    public get xyResolution(): number {
        return this._segmentResolution;
    }

    /**
     * Method to get the margin around the structure outline for distance transform.\
     */
    public get margin(): number {
        return this._margin;
    }

    public get signedDistanceField(): SignedDistanceField | undefined {
        return this._sdf;
    }

    public get patientToImage(): ReadonlyMat4 {
        return this._patientToImage;
    }

    public get textureGrid(): Grid {
        return this._textureGrid;
    }

    /**
     * Method to get a {@link RotatedBoundingBox} that surrounds the whole {@link ContourTexture}.
     */
    public get fullBoundingBox(): RotatedBoundingBox {
        return this._boundingBox;
    }

    /**
     * Method to get a {@link RotatedBoundingBox} that surrounds only the actual visible contents of the {@link ContourTexture}.\
     * This is same as the {@link fullBoundingBox} minus margins.
     */
    public get contentBoundingBox(): RotatedBoundingBox {
        return new RotatedBoundingBox(this._boundingBox).addMargins(
            -this._margin
        );
    }

    public toImageSpace(patient: ReadonlyVec3): vec3 {
        return vec3.transformMat4(vec3.create(), patient, this._patientToImage);
    }

    public toPatientSpace(image: ReadonlyVec3): vec3 {
        return vec3.transformMat4(vec3.create(), image, this.imageToPatient);
    }

    public patientCoordsToImageIndices(_patientCoords: vec3): vec2 {
        throw new Error('Method not implemented.');
    }

    public patientCoordsToSdfIndices(patientCoords: ReadonlyVec3): vec3 {
        return this._textureGrid.toVoxelSpace(patientCoords);
    }

    public sliceIndexToNormalPosition(index: number): number {
        if (index < 0 || index >= this._textureGrid.sizeZ) {
            throw new Error(
                `Attempt to convert slice index ${index} to normal position for ContourTexture with ${this._textureGrid.sizeZ} slices`
            );
        }
        if (!Number.isInteger(index)) {
            throw new Error(
                `Attempt to convert non-integer slice index ${index} to normal position`
            );
        }
        return (
            this._textureGrid.getPosition()[2] + index * this._textureGrid.resZ
        );
    }

    public generateVertex(x: 1 | -1, y: 1 | -1, normalPosition: number): vec3 {
        if (!this._boundingBox.min || !this._boundingBox.max) {
            throw new Error(
                `Attempt to generate vertex for ContourTexture with an empty BoundingBox`
            );
        }
        return vec3.transformMat3(
            vec3.create(),
            vec3.fromValues(
                x === -1 ? this._boundingBox.min[0] : this._boundingBox.max[0],
                y === -1 ? this._boundingBox.min[1] : this._boundingBox.max[1],
                normalPosition
            ),
            this.rotation
        );
    }

    /**
     * Method to calculate the intersection between this texture and a projection plane.\
     */
    public crossSection(
        toProjection: ReadonlyMat4,
        transformation?: ReadonlyMat4
    ): VertexArrayObject | undefined {
        return this._boundingBox.crossSection(
            toProjection,
            transformation,
            this._textureGrid.sizeZ
        );
    }

    public invalidateCachedPixels() {
        this._pixels = undefined;
        this._volumeCachedCm3 = undefined;
    }

    public get pixels(): Uint8Array[] {
        if (!this._pixels) {
            this._pixels = this._sdf.toArray();
        }
        return this._pixels;
    }

    public get changedPixels(): Uint8Array[] | undefined {
        const previousPixels = this._pixels;
        this._pixels = this._sdf.toArray();
        return previousPixels;
    }

    /**
     * Method to {@link SignedDistanceField.bind bind} the {@link sdf} {@link WebGLTexture texture} of this {@link ContourTexture}.
     * @returns True if the {@link sdf} was successfully bound, false otherwise.
     */
    public bindTexture(): boolean {
        return this._sdf ? this._sdf.bind() : false;
    }

    /**
     * Method to unbind the {@link SignedDistanceField} texture of this {@link ContourTexture}.\
     * This will unbind any texture that has been bound.
     */
    public unbindTexture(): void {
        this._sdf?.unbind();
    }

    /**
     * Regenerate the texture from the contour sequence.
     */
    public update(contourSequence: ContourSequence): string | undefined {
        if (contourSequence.contourSlices.length === 0) {
            return;
        }

        let min = vec3.fromValues(
                Number.MAX_SAFE_INTEGER,
                Number.MAX_SAFE_INTEGER,
                Number.MAX_SAFE_INTEGER
            ),
            max = vec3.fromValues(
                Number.MIN_SAFE_INTEGER,
                Number.MIN_SAFE_INTEGER,
                Number.MIN_SAFE_INTEGER
            );

        const imageGrid = this._imageGrid;

        // find the bounding box of the contour sequence in image space
        for (const slice of contourSequence.contourSlices) {
            for (const contour of slice.contours) {
                for (const vertex of contour) {
                    const v = imageGrid.rotateInverse(vertex);
                    vec3.min(min, min, v);
                    vec3.max(max, max, v);
                }
            }
        }

        // add margin
        const marginv = vec3.fromValues(
            this._margin,
            this._margin,
            this._margin
        );

        vec3.add(max, max, marginv);
        vec3.sub(min, min, marginv);

        const rotation = imageGrid.getOrientation();
        this._boundingBox.rotate(rotation);

        // adjust the bounding box to pixel boundaries, result is in patient space
        min = this._imageGrid.toWorldSpace(
            vec3.floor(
                vec3.create(),
                this._imageGrid.toVoxelSpace(imageGrid.rotate(min))
            )
        );
        max = this._imageGrid.toWorldSpace(
            vec3.ceil(
                vec3.create(),
                this._imageGrid.toVoxelSpace(imageGrid.rotate(max))
            )
        );

        // bounding box in image space
        this._boundingBox.min = imageGrid.rotateInverse(min);
        this._boundingBox.max = imageGrid.rotateInverse(max);

        const sizePx = vec3.create();
        vec3.set(
            sizePx,
            Math.ceil(this._boundingBox.size[0] / this.xyResolution),
            Math.ceil(this._boundingBox.size[1] / this.xyResolution),
            Math.round(this._boundingBox.size[2] / this._imageGrid.resZ) + 1
        );

        if (this._sdf) {
            this._sdf.resize(sizePx);
        } else {
            this._sdf = new SignedDistanceField(sizePx);
        }

        const scale = vec3.scale(vec3.create(), this._boundingBox.size, 0.5);

        mat4.fromRotationTranslationScale(
            this.imageToPatient,
            quat.fromMat3(quat.create(), rotation),
            imageGrid.rotate(this._boundingBox.center),
            scale
        );

        mat4.invert(this._patientToImage, this.imageToPatient);

        this._textureGrid = new Grid(
            sizePx[0],
            sizePx[1],
            sizePx[2],
            this._segmentResolution,
            this._segmentResolution,
            this._imageGrid.resZ,
            mat4.fromRotationTranslation(
                mat4.create(),
                quat.fromMat3(quat.create(), rotation),
                imageGrid.rotate(this._boundingBox.min)
            )
        );

        const renderErrors = new SDFMesh().renderSDF(
            this,
            contourSequence,
            undefined,
            this._margin,
            this._margin
        );
        this.invalidateCachedPixels();
        return renderErrors;
    }

    public reset(boundingBox: RotatedBoundingBox, sdf: SignedDistanceField) {
        this._sdf = sdf;
        this._boundingBox = boundingBox;
        if (!this._boundingBox.min || !this._boundingBox.max) {
            throw new Error(
                `Attempt to generate slices for ContourTexture with an empty BoundingBox`
            );
        }

        // ensure the grid aligns with image grid. TODO: simplify this to use the image grid directly
        const bbMinImageGrid = this._imageGrid.toVoxelSpace(
            this._boundingBox.min
        );
        const bbMinImageGridClamped = this._imageGrid.toWorldSpace(
            vec3.floor(bbMinImageGrid, bbMinImageGrid)
        );
        this._boundingBox.min[2] = bbMinImageGridClamped[2];

        const bbMaxImageGrid = this._imageGrid.toVoxelSpace(
            this._boundingBox.max
        );
        const bbMaxImageGridClamped = this._imageGrid.toWorldSpace(
            vec3.floor(bbMaxImageGrid, bbMaxImageGrid)
        );
        this._boundingBox.max[2] = bbMaxImageGridClamped[2];

        const sizePx = vec3.create();
        vec3.set(
            sizePx,
            Math.ceil(this._boundingBox.size[0] / this.xyResolution),
            Math.ceil(this._boundingBox.size[1] / this.xyResolution),
            Math.round(this._boundingBox.size[2] / this._imageGrid.resZ) + 1
        );
        mat4.fromRotationTranslationScale(
            this.imageToPatient,
            quat.fromMat3(quat.create(), this.rotation),
            vec3.transformMat3(
                vec3.create(),
                this._boundingBox.center,
                this._boundingBox.rotation
            ),
            vec3.scale(vec3.create(), this._boundingBox.size, 0.5)
        );

        mat4.invert(this._patientToImage, this.imageToPatient);

        this._textureGrid = new Grid(
            sizePx[0],
            sizePx[1],
            sizePx[2],
            this._segmentResolution,
            this._segmentResolution,
            this._imageGrid.resZ,
            mat4.fromRotationTranslation(
                mat4.create(),
                quat.fromMat3(quat.create(), this.rotation),
                this._boundingBox.min
            )
        );
        this.invalidateCachedPixels();
    }

    public resetGeometry(texture: ContourTexture) {
        this._boundingBox = new RotatedBoundingBox(texture._boundingBox);
        if (!this._boundingBox.min || !this._boundingBox.max) {
            throw new Error(
                `Attempt to generate slices for ContourTexture with an empty BoundingBox`
            );
        }
        this._sdf = new SignedDistanceField(texture._sdf.size);
        mat4.fromRotationTranslationScale(
            this.imageToPatient,
            quat.fromMat3(quat.create(), this.rotation),
            vec3.transformMat3(
                vec3.create(),
                this._boundingBox.center,
                this._boundingBox.rotation
            ),
            vec3.scale(vec3.create(), this._boundingBox.size, 0.5)
        );
        const sizePx = texture._sdf.size;
        mat4.invert(this._patientToImage, this.imageToPatient);

        this._textureGrid = new Grid(
            sizePx[0],
            sizePx[1],
            sizePx[2],
            this._segmentResolution,
            this._segmentResolution,
            this._imageGrid.resZ,
            mat4.fromRotationTranslation(
                mat4.create(),
                quat.fromMat3(quat.create(), this.rotation),
                vec3.transformMat3(
                    vec3.create(),
                    this._boundingBox.min,
                    this._boundingBox.rotation
                )
            )
        );
        this._margin = texture.margin;
        this.invalidateCachedPixels();
    }

    public resetFromPixels(
        grid: Grid,
        bb: RotatedBoundingBox,
        pixels: Uint8Array[]
    ) {
        this._boundingBox = bb;
        mat4.fromRotationTranslationScale(
            this.imageToPatient,
            quat.fromMat3(quat.create(), this.rotation),
            vec3.transformMat3(
                vec3.create(),
                this._boundingBox.center,
                this._boundingBox.rotation
            ),
            vec3.scale(vec3.create(), this._boundingBox.size, 0.5)
        );
        mat4.invert(this._patientToImage, this.imageToPatient);
        this._sdf = new SignedDistanceField(grid.getSize());
        this._sdf.arrayToTexture(pixels, 0);
        this._textureGrid = grid;
        this.invalidateCachedPixels();
    }

    /**
     * Method to delete this {@link ContourTexture}.\
     * This de-allocates all the WebGL memory reserved by this {@link ContourTexture}.\
     * Currently that only means deleting the {@link SignedDistanceField}.
     */
    public delete() {
        this._sdf?.delete();
    }
}
